import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, doc, updateDoc, query, orderBy } from "firebase/firestore";
import { db } from "../services/firebase";

const fmtXOF = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "XOF" });

const STATUSES = [
  { v: "pending",   label: "En attente" },
  { v: "paid",      label: "Payée" },
  { v: "shipped",   label: "Expédiée" },
  { v: "cancelled", label: "Annulée" },
];

export default function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [qText, setQText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expanded, setExpanded] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // tri par date desc si index dispo, sinon tu peux retirer orderBy
      const col = collection(db, "orders");
      const qy = query(col, orderBy("createdAt", "desc"));
      const snap = await getDocs(qy);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setOrders(list);
    } catch (e) {
      console.error(e);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const filtered = useMemo(() => {
    const t = qText.trim().toLowerCase();
    return orders.filter((o) => {
      const okStatus = statusFilter ? (o.status === statusFilter) : true;
      if (!okStatus) return false;
      if (!t) return true;
      const hay = [
        o.customerName,
        o.phone,
        o.address,
        ...(Array.isArray(o.items) ? o.items.map((i) => `${i.name} ${i.id}`) : []),
        o.id,
      ].join(" ").toLowerCase();
      return hay.includes(t);
    });
  }, [orders, qText, statusFilter]);

  const changeStatus = async (id, status) => {
    setUpdating((u) => ({ ...u, [id]: true }));
    try {
      await updateDoc(doc(db, "ordersWine", id), { status });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    } catch (e) {
      console.error(e);
      alert("Impossible de mettre à jour le statut.");
    } finally {
      setUpdating((u) => ({ ...u, [id]: false }));
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h3 className="m-0">Commandes</h3>
        <button className="btn btn-sm btn-outline-secondary" onClick={fetchOrders} disabled={loading}>
          {loading ? "Chargement…" : "↻ Actualiser"}
        </button>
      </div>

      {/* Filtres */}
      <div className="row g-2 mb-3">
        <div className="col-12 col-md-6">
          <input
            className="form-control"
            placeholder="Rechercher (client, téléphone, adresse, produit…) "
            value={qText}
            onChange={(e) => setQText(e.target.value)}
          />
        </div>
        <div className="col-6 col-md-3">
          <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Tous les statuts</option>
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-muted">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="alert alert-light border">Aucune commande.</div>
      ) : (
        <div className="list-group">
          {filtered.map((o) => {
            const created = o.createdAt?.toDate ? o.createdAt.toDate() : (o.createdAt || null);
            const dateText = created ? new Date(created).toLocaleString("fr-FR") : "—";
            const total = typeof o.total === "number"
              ? o.total
              : Array.isArray(o.items) ? o.items.reduce((s, i) => s + (Number(i.price)||0)*(i.qty||0), 0) : 0;
            const itemsCount = Array.isArray(o.items) ? o.items.reduce((s, i) => s + (i.qty || 0), 0) : 0;

            return (
              <div key={o.id} className="list-group-item">
                <div className="d-flex align-items-start justify-content-between">
                  <div className="me-3">
                    <div className="fw-semibold">{o.customerName || "Client inconnu"}</div>
                    <div className="small text-muted">
                      {o.phone || "—"} {o.address ? `• ${o.address}` : ""}
                    </div>
                    <div className="small text-muted">#{o.id} • {dateText}</div>
                  </div>

                  <div className="text-end">
                    <div className="fw-semibold">{fmtXOF.format(total)}</div>
                    <div className="small text-muted">{itemsCount} article{itemsCount>1?"s":""}</div>
                    <div className="mt-2">
                      <select
                        className="form-select form-select-sm"
                        value={o.status || "pending"}
                        onChange={(e) => changeStatus(o.id, e.target.value)}
                        disabled={!!updating[o.id]}
                      >
                        {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Toggle détails */}
                <div className="mt-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setExpanded((cur) => (cur === o.id ? null : o.id))}
                  >
                    {expanded === o.id ? "Masquer le détail" : "Voir le détail"}
                  </button>
                </div>

                {expanded === o.id && (
                  <div className="mt-3">
                    {Array.isArray(o.items) && o.items.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm align-middle">
                          <thead>
                            <tr>
                              <th>Produit</th>
                              <th className="text-center">Qté</th>
                              <th className="text-end">PU</th>
                              <th className="text-end">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {o.items.map((it, idx) => {
                              const pu = Number(it.price) || 0;
                              const qt = Number(it.qty) || 0;
                              return (
                                <tr key={`${it.id}-${idx}`}>
                                  <td>{it.name || it.id}</td>
                                  <td className="text-center">{qt}</td>
                                  <td className="text-end">{fmtXOF.format(pu)}</td>
                                  <td className="text-end">{fmtXOF.format(pu * qt)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-muted">Aucun article.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
